import { useState } from "react";
import { Mail, Phone, MapPin, Send, Facebook, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { CONTACT } from "@/lib/contact";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="hero-bg pt-32 pb-16 noise" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6">
          <p className="kicker">Get in Touch</p>
          <h1 className="font-display text-5xl sm:text-7xl mt-4">
            Talk to <em className="not-italic text-gold-gradient">Us</em>
          </h1>
          <p className="mt-4 text-foreground/65 max-w-xl">
            Question about a vehicle? Need help planning a road trip? We're here.
          </p>
        </div>
      </section>

      <section className="section-bg-dark py-16">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 glass-card rounded-2xl p-8 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Name *</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-ink-800/60 border-gold-500/20" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Email *</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-ink-800/60 border-gold-500/20" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Phone</Label>
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 bg-ink-800/60 border-gold-500/20" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Subject</Label>
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 bg-ink-800/60 border-gold-500/20" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Message *</Label>
              <Textarea
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                className="mt-2 bg-ink-800/60 border-gold-500/20"
              />
            </div>
            <button type="submit" disabled={submitting} className="btn-gold w-full sm:w-auto disabled:opacity-60" data-testid="contact-submit">
              <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-4">
            <a href="https://maps.google.com/?q=Puerto+Princesa+Palawan" target="_blank" rel="noreferrer" className="glass-card rounded-2xl p-6 flex items-start gap-4 hover:border-gold-500/40 transition" data-testid="contact-location">
              <MapPin className="w-5 h-5 text-gold-400 mt-0.5" />
              <div>
                <p className="font-sans-display text-sm uppercase tracking-widest text-gold-300">Visit Us</p>
                <p className="text-foreground mt-2">Puerto Princesa, Palawan</p>
                <p className="text-xs text-foreground/55 mt-1">Philippines</p>
              </div>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="glass-card rounded-2xl p-6 flex items-start gap-4 hover:border-gold-500/40 transition">
              <Mail className="w-5 h-5 text-gold-400 mt-0.5" />
              <div>
                <p className="font-sans-display text-sm uppercase tracking-widest text-gold-300">Email</p>
                <p className="text-foreground mt-2">{CONTACT.email}</p>
                <p className="text-xs text-foreground/55 mt-1">We reply within 24 hours</p>
              </div>
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} className="glass-card rounded-2xl p-6 flex items-start gap-4 hover:border-gold-500/40 transition">
              <Phone className="w-5 h-5 text-gold-400 mt-0.5" />
              <div>
                <p className="font-sans-display text-sm uppercase tracking-widest text-gold-300">Call / Text</p>
                <p className="text-foreground mt-2">{CONTACT.phone}</p>
                <p className="text-xs text-foreground/55 mt-1">Mon–Sun · 7:00 AM – 9:00 PM</p>
              </div>
            </a>
            <a href={`https://wa.me/${CONTACT.whatsappRaw.replace("+", "")}`} target="_blank" rel="noreferrer" className="glass-card rounded-2xl p-6 flex items-start gap-4 hover:border-gold-500/40 transition">
              <MessageCircle className="w-5 h-5 text-gold-400 mt-0.5" />
              <div>
                <p className="font-sans-display text-sm uppercase tracking-widest text-gold-300">WhatsApp / Viber</p>
                <p className="text-foreground mt-2">{CONTACT.whatsapp}</p>
                <p className="text-xs text-foreground/55 mt-1">Fastest response</p>
              </div>
            </a>
            <div className="glass-card rounded-2xl p-6">
              <p className="font-sans-display text-sm uppercase tracking-widest text-gold-300">Connect</p>
              <div className="mt-4 flex gap-3">
                <a href={CONTACT.facebookUrl} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full border border-gold-500/30 flex items-center justify-center hover:bg-gold-500/10 transition" data-testid="contact-facebook">
                  <Facebook className="w-5 h-5 text-gold-300" />
                </a>
                <a href={`https://wa.me/${CONTACT.whatsappRaw.replace("+", "")}`} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-full border border-gold-500/30 flex items-center justify-center hover:bg-gold-500/10 transition">
                  <MessageCircle className="w-5 h-5 text-gold-300" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
